import { ColDef, GridOptions } from '@ag-grid-community/core';
import { Component, OnInit } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { AutoUnsubscribe } from 'projects/cupcake/src/app/core/auto-unsubscribe';
import { SubSink } from 'subsink';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FormlyDocumentAttachmentsService } from './formly-document-attachments.service';
import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { NgxSpinnerService } from 'ngx-spinner';
import { AgGridDocumentAttachmentActionRenderer } from './ag-grid-document-attachment-action-renderer/ag-grid-document-attachment-action-renderer.component';
import { MatHint } from '@angular/material/form-field';
import { MatCard } from '@angular/material/card';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatFabButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

@AutoUnsubscribe()
@Component({
  selector: 'app-formly-document-attachments',
  templateUrl: './formly-document-attachments.component.html',
  styleUrls: ['./formly-document-attachments.component.scss'],
  providers: [FormlyDocumentAttachmentsService],
  imports: [
    MatHint,
    MatCard,
    NgxFileDropModule,
    AgGridModule,
    MatFabButton,
    MatTooltip,
    MatIcon,
    TranslateModule,
  ],
})
export class FormlyDocumentAttachmentsComponent
  extends FieldType
  implements OnInit
{
  subs = new SubSink();

  public get isReadOnly(): boolean {
    return this.field?.formControl?.disabled == true;
  }

  columnDefs: ColDef[] = [
    {
      headerName: _('SHARED.DOCUMENT_ATTACHMENTS.GRID.COL.FILENAME'),
      field: 'documentFilename',
      flex: 1,
    },
    {
      headerName: _('SHARED.DOCUMENT_ATTACHMENTS.GRID.COL.CREATION_DATE'),
      field: 'documentCreationDate',
      width: 180,
      hide: false,
      valueFormatter: this.documentAttachmentsService.dateValueFormatter.bind(
        this.documentAttachmentsService
      ),
    },
    {
      headerName: _('SHARED.DOCUMENT_ATTACHMENTS.GRID.COL.USER'),
      field: 'userId',
      flex: 1,
    },
    {
      headerName: _('SHARED.DOCUMENT_ATTACHMENTS.GRID.COL.ACTION'),
      field: 'id',
      cellRenderer: 'documentAttachmentActionRenderer',
    },
  ];

  defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    headerValueGetter: params =>
      this.documentAttachmentsService.localizeHeader(params),
  };

  gridOptions: GridOptions = {
    defaultColDef: this.defaultColDef,
    columnDefs: this.columnDefs,
    domLayout: 'autoHeight',
    components: {
      documentAttachmentActionRenderer: AgGridDocumentAttachmentActionRenderer,
    },
    context: {
      readOnly: this.isReadOnly,
    },
  };

  constructor(
    private documentAttachmentsService: FormlyDocumentAttachmentsService,
    private spinnerService: NgxSpinnerService
  ) {
    super();
    this.documentAttachmentsService.bindGridEvents(this.gridOptions);
  }

  ngOnInit(): void {
    this.documentAttachmentsService.documentIds$.next(this.formControl.value);

    this.subs.sink = this.documentAttachmentsService.documentIds$.subscribe(s =>
      this.formControl.setValue(s)
    );

    this.gridOptions.context.readOnly = this.isReadOnly;
    this.documentAttachmentsService.gridApi?.refreshCells({ force: true });

    this.subs.sink = this.field.form?.statusChanges.subscribe(() => {
      if (this.gridOptions.context.readOnly != this.isReadOnly) {
        this.gridOptions.context.readOnly = this.isReadOnly;
        this.documentAttachmentsService.gridApi?.refreshCells({
          force: true,
        });
      }
    });
  }

  public dropped(files: NgxFileDropEntry[]) {
    const fileEntry = files[0].fileEntry as FileSystemFileEntry;
    fileEntry.file((file: File) => {
      this.spinnerService.show();
      this.subs.sink = this.documentAttachmentsService
        .addDocument(file)
        .subscribe(() => this.spinnerService.hide());
    });
  }
}
